import { useRef, useEffect, useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Eraser, Check } from "lucide-react";

interface SignaturePadProps {
    onSave: (dataUrl: string) => void;
    /**
     * Controlled accepted signature. When set, the pad renders the captured
     * image in the SAME frame (canvas box + hint + button row) instead of
     * collapsing to a small thumbnail — accepting a signature must not
     * reflow the inputs below it, or mid-typing values land in the wrong
     * fields. Pass `onClear` to let the user redo the signature.
     */
    value?: string | null;
    onClear?: () => void;
    width?: number;
    height?: number;
    className?: string;
}

export function SignaturePad({
    onSave,
    value = null,
    onClear,
    width = 400,
    height = 200,
    className,
}: SignaturePadProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isDrawing = useRef(false);
    const lastPoint = useRef<{ x: number; y: number } | null>(null);
    const [hasStrokes, setHasStrokes] = useState(false);

    const getCtx = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.strokeStyle = "#1e3a5f";
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        return ctx;
    }, []);

    const getPoint = useCallback((e: MouseEvent | TouchEvent): { x: number; y: number } | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        if ("touches" in e) {
            const touch = e.touches[0];
            if (!touch) return null;
            return {
                x: (touch.clientX - rect.left) * scaleX,
                y: (touch.clientY - rect.top) * scaleY,
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    }, []);

    const startStroke = useCallback(
        (e: MouseEvent | TouchEvent) => {
            e.preventDefault();
            isDrawing.current = true;
            lastPoint.current = getPoint(e);
        },
        [getPoint],
    );

    const draw = useCallback(
        (e: MouseEvent | TouchEvent) => {
            if (!isDrawing.current) return;
            e.preventDefault();
            const ctx = getCtx();
            const point = getPoint(e);
            if (!ctx || !point || !lastPoint.current) return;
            ctx.beginPath();
            ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
            ctx.lineTo(point.x, point.y);
            ctx.stroke();
            lastPoint.current = point;
            if (!hasStrokes) setHasStrokes(true);
        },
        [getCtx, getPoint, hasStrokes],
    );

    const endStroke = useCallback(() => {
        isDrawing.current = false;
        lastPoint.current = null;
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const onMouseDown = (e: MouseEvent) => startStroke(e);
        const onMouseMove = (e: MouseEvent) => draw(e);
        const onMouseUp = () => endStroke();
        const onTouchStart = (e: TouchEvent) => startStroke(e);
        const onTouchMove = (e: TouchEvent) => draw(e);
        const onTouchEnd = () => endStroke();

        canvas.addEventListener("mousedown", onMouseDown);
        canvas.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        canvas.addEventListener("touchstart", onTouchStart, { passive: false });
        canvas.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("touchend", onTouchEnd);

        return () => {
            canvas.removeEventListener("mousedown", onMouseDown);
            canvas.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            canvas.removeEventListener("touchstart", onTouchStart);
            canvas.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
        };
        // `value` is a dep because the canvas unmounts while a signature is
        // shown — listeners must re-attach to the fresh canvas after Clear.
    }, [startStroke, draw, endStroke, value]);

    const clear = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasStrokes(false);
    }, []);

    const save = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !hasStrokes) return;
        onSave(canvas.toDataURL("image/png"));
    }, [hasStrokes, onSave]);

    // The canvas unmounts while a value is shown, so strokes are gone when
    // the user clears — make sure the "Accept" button is disabled again.
    useEffect(() => {
        if (value) setHasStrokes(false);
    }, [value]);

    // Accepted state renders in the exact same frame (image box with the
    // canvas's aspect ratio + hint line + h-11 button row) so swapping
    // pad <-> accepted never changes the component's height.
    if (value) {
        return (
            <div className={cn("flex flex-col gap-3", className)}>
                <div className="rounded-lg border border-border bg-white overflow-hidden">
                    <img
                        src={value}
                        alt="captured signature"
                        className="w-full object-contain"
                        style={{ aspectRatio: `${width}/${height}` }}
                    />
                </div>
                <p className="text-xs text-foreground/80 text-center">
                    <Check className="inline h-3.5 w-3.5 mr-1 text-emerald-600" />
                    Signature captured
                </p>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1 h-11" onClick={onClear}>
                        <Eraser className="h-4 w-4 mr-1.5" />
                        Clear &amp; redo
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={cn("flex flex-col gap-3", className)}>
            <div className="rounded-lg border border-border bg-white overflow-hidden touch-none">
                <canvas
                    ref={canvasRef}
                    width={width}
                    height={height}
                    className="w-full cursor-crosshair"
                    style={{ aspectRatio: `${width}/${height}` }}
                />
            </div>
            <p className="text-xs text-foreground/80 text-center">Sign above using your finger or mouse</p>
            <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1 h-11" onClick={clear}>
                    <Eraser className="h-4 w-4 mr-1.5" />
                    Clear
                </Button>
                <Button type="button" className="flex-1 h-11" onClick={save} disabled={!hasStrokes}>
                    <Check className="h-4 w-4 mr-1.5" />
                    Accept Signature
                </Button>
            </div>
        </div>
    );
}
